import Text from '@axa-japan/design-system-react/Text';
import { useEffect, useRef, useState } from 'react';
import Webcam from 'react-webcam';
import crossIconUrl from '../../assets/cross_icon.svg';
import useOrientation from '../../hooks/useOrientation';
import { useUserContext } from '../../providers/UserProvider';
import { parseCameraError } from '../../utils/cameraErroMapping';
import CustomModal from '../common/CustomModal';
import OrientationWarning from '../common/OrientationWarning/OrientationWarning';

const videoConstraints = {
  height: { ideal: 2988 },
  width: { ideal: 5312 },
  facingMode: 'environment', // Default to back camera
};

type CameraProps = {
  onCapture: (file: File, src: string | null) => void;
  onClose: () => void;
};

const Camera = ({ onCapture, onClose }: CameraProps) => {
  const webcamRef = useRef<Webcam>(null);
  const deviceOrientation = useOrientation();
  const isLandscape = deviceOrientation === 'landscape';

  const { setLoader } = useUserContext();

  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [capturedImgFile, setCapturedImgFile] = useState<File>();
  const [capturedImgSrc, setCapturedImgSrc] = useState<string | null>();

  useEffect(() => {
    // enable loader until camera initializes
    setLoader(isLandscape);
  }, [deviceOrientation]);

  const capture = async () => {
    try {
      if (webcamRef.current) {
        const imageSrc = webcamRef.current.getScreenshot();
        const blob = await fetch(imageSrc as string).then((res) => res.blob());
        const file = new File([blob], 'camera.png', { type: 'image/png', lastModified: parseInt(new Date().toDateString(), 10) });
        setCapturedImgSrc(imageSrc);
        setCapturedImgFile(file);
      }
    } catch (e) {
      setIsErrorModalOpen(true);
      setErrorMessage(`${e}`);
    }
  };

  if (deviceOrientation === 'portrait') {
    return <OrientationWarning />;
  }
  return (
    <div className="m-auto w-full flex flex-col justify-between h-full bg-white">
      <CustomModal buttonLabel="OK" isWarningModal open={isErrorModalOpen} title="カメラを起動できません。" onClose={onClose}>
        <div>{errorMessage}</div>
      </CustomModal>
      <div
        onClick={() => {
          onClose();
        }}
        className="fixed top-5 right-5"
      >
        <img className="w-10" src={crossIconUrl} />
      </div>

      <div className="flex flex-row !w-full h-full justify-around items-center gap-4 pb-10 pt-20 px-6">
        <div className="h-full w-full bg-interactiveVisitedGrey flex items-center justify-center">
          <Webcam
            className="transition delay-150 duration-300 ease-in-out"
            audio={false}
            ref={webcamRef}
            onUserMedia={() => {
              setTimeout(() => {
                setLoader(false);
              }, 100);
            }}
            onUserMediaError={(e) => {
              setErrorMessage(parseCameraError(`${e}`));
              setLoader(false);
              setIsErrorModalOpen(true);
            }}
            screenshotFormat="image/jpeg"
            videoConstraints={{ ...videoConstraints }}
          />

          {capturedImgSrc && <img src={capturedImgSrc} className="fixed z-10" />}
        </div>
        {capturedImgSrc ? (
          <div className="h-full min-w-52 rounded flex flex-col gap-4 justify-center items-center">
            <div
              className="bg-interactiveActivePrimary h-1/2 w-full items-center flex justify-center"
              onClick={() => onCapture(capturedImgFile as File, capturedImgSrc)}
            >
              <Text size="lg" weight="bold" color="primary-white">
                はい
              </Text>
            </div>

            <div className="bg-interactiveActivePrimary h-1/2 w-full flex justify-center items-center" onClick={() => setCapturedImgSrc(null)}>
              <Text size="lg" weight="bold" color="primary-white">
                画像を撮り直す
              </Text>
            </div>
          </div>
        ) : (
          <div className="bg-interactiveActivePrimary h-full min-w-52 rounded flex justify-center items-center" onClick={capture}>
            <Text size="lg" weight="bold" color="primary-white">
              端末カメラで撮影
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default Camera;
